import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SEO from '../../../../shared/components/SEO/SEO';
import { useAuth } from '../../../../contexts/AuthContext';
import { forumAPI, type ForumPost } from '../../../../services/api';
import { stripHtml } from './utils';
import { UserAvatar } from './UserAvatar';
import './Community.css';

const MyPosts: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [posts, setPosts] = useState<ForumPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login?next=' + encodeURIComponent('/community/my-posts'), { replace: true });
      return;
    }
    if (!isAuthenticated) return;
    setLoading(true);
    forumAPI.myPosts()
      .then((res) => setPosts(res.data as ForumPost[]))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, [isAuthenticated, authLoading, navigate]);

  if (authLoading || !isAuthenticated) {
    return (
      <div className="community-page">
        <div className="community-empty">Loading...</div>
      </div>
    );
  }

  return (
    <>
      <SEO title="My posts - Crypxa Community" description="Your forum posts." />
      <div className="community-page">
        <div className="community-header">
          <h1>My posts</h1>
          <Link to="/community" className="community-btn secondary">Back to Community</Link>
        </div>
        {loading ? (
          <div className="community-empty">Loading...</div>
        ) : posts.length === 0 ? (
          <div className="community-empty">
            You haven’t posted yet.
            <Link to="/community/new" className="community-btn" style={{ marginTop: '1rem' }}>Create a post</Link>
          </div>
        ) : (
          <div className="community-post-list">
            {posts.map((post) => (
              <div key={post.id} className="community-post-card">
                <div className="community-card-media">
                  {post.attachments && post.attachments.length > 0 ? (
                    <img src={post.attachments[0].file_url} alt="" />
                  ) : (
                    <div className="community-card-placeholder">
                      <span className="community-card-placeholder-icon" aria-hidden>🖼️</span>
                      <span>No image</span>
                    </div>
                  )}
                </div>
                <div className="community-card-body">
                  <div className="meta">
                    <span className={`status-badge status-${post.status}`}>{post.status}</span>
                    <UserAvatar user={post.user} />
                    <span>{post.user.display_name}</span>
                    <span>{new Date(post.created_at).toLocaleDateString()}</span>
                  </div>
                <h3>{post.title}</h3>
                <p className="excerpt">{stripHtml(post.body).slice(0, 150)}{stripHtml(post.body).length > 150 ? '...' : ''}</p>
                {post.attachments && post.attachments.length > 1 && (
                  <div className="community-card-images">
                    {post.attachments.slice(1, 5).map((att) => (
                      <span key={att.id} className="community-card-thumb">
                        <img src={att.file_url} alt="" />
                      </span>
                    ))}
                    {post.attachments.length > 5 && (
                      <span className="community-card-thumb community-card-thumb-more">+{post.attachments.length - 5}</span>
                    )}
                  </div>
                )}
                {post.status === 'rejected' && (
                  <p style={{ fontSize: '0.85rem', color: 'var(--color-Error, #b91c1c)', marginTop: '0.5rem' }}>
                    Rejected — you cannot edit this post. Create a new post if you’d like to share again.
                  </p>
                )}
                <div className="stats community-stats-with-actions">
                  <span className="community-stat-item stat-likes">{(post.like_count ?? 0) > 0 ? <i className="fas fa-heart" aria-hidden></i> : null} {post.like_count ?? 0}</span>
                  <span className="community-stat-item stat-comments"><i className="fas fa-comment" aria-hidden></i> {post.comment_count ?? 0}</span>
                  <span className="community-stat-item stat-views"><i className="fas fa-eye" aria-hidden></i> {post.views}</span>
                  <span style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <Link to={`/community/p/${post.id}`} className="community-btn secondary" style={{ padding: '0.4rem 0.8rem', fontSize: '0.85rem' }}><i className="fas fa-external-link-alt" style={{ marginRight: '0.35rem' }} aria-hidden></i>View</Link>
                    {post.status === 'pending' && (
                      <Link to={`/community/edit/${post.id}`} className="community-btn secondary" style={{ padding: '0.4rem 0.8rem', fontSize: '0.85rem' }}><i className="fas fa-edit" style={{ marginRight: '0.35rem' }} aria-hidden></i>Edit</Link>
                    )}
                  </span>
                </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default MyPosts;
